import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ImageCarousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-advance the carousel every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(timer);
  }, [currentIndex]);

  const handlePrevious = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const handleNext = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const goToSlide = (index) => {
    if (!isTransitioning && index !== currentIndex) {
      setIsTransitioning(true);
      setCurrentIndex(index);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  return (
    <>
      <div className="relative w-full mx-auto overflow-hidden rounded-lg shadow-1">
        {/* Main carousel container */}
        <div className="text-main mt-5 text-center px-5 text-2xl font-bold ml-3">
          Our clients pics 📸{" "}
        </div>
        <div className="relative h-96">
          {/* Images container with transition */}
          <div
            className="absolute w-full h-full flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {items.map((url, index) => (
              <div
                key={index}
                className="min-w-full h-full flex items-center justify-center p-8"
              >
                <img
                  src={url}
                  alt={`Slide ${index + 1}`}
                  className="max-w-full max-h-full object-contain "
                />
              </div>
            ))}
          </div>

          {/* Navigation buttons */}
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
            disabled={isTransitioning}
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
            disabled={isTransitioning}
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </button>

          {/* Dots navigation */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-blue-500 w-4"
                    : "bg-gray-400 hover:bg-gray-600"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default function ModernCarousel() {
  const images = [
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/jr5josm5ogyck0ns00wx.png",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/po3d3xsk3vklukuuhdjy",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/le0rhvvzastgdiv3beuj",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/k7anlzjk7g97rcxqlt0g",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/zsyi83sjyhp4rydj8jfq",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/oz67l2h86ccsjlnrqwaz",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/zdoiiseidx7lej1jtkmd",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/yijuhhtgicrdlxgawbxk",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/smmxxzeiwsykwdks5ykz",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/owx56czevdgut1donmxs",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/tvpflw2qyuyklpwhfsjp",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/mcjppic9ipwm3r0nohih",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/jaqujjpkhdpq5iraqkln",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/jzlcr8qtlxiahg8dltia",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/avm8e5msy4wivnteasjw",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/oje7ia3wng27sl5d3ya8",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/p1vyuodj5mwcotwclh8k",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/bsjkttqaeabyjcjyex2v",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/x9jd5cnfwys0vqgvl3em",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/qqgb6pcu72amfgghabwh",
    "https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735394825/ecommerce/Our%20Clients%20Pics/gnwu05jynsnfuy9qtbau",
  ];

  return (
    <div className="p-4">
      <ImageCarousel items={images} />
    </div>
  );
}
